import { useEffect, useRef, useState } from "react";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "react-router";
import { Form, redirect, useActionData, useNavigation } from "react-router";
import { getAppToken } from "~/.server/token";
import { signInWithEmailCode, startEmailSignIn } from "~/.server/user";
import { Header } from "~/components/header";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";

export async function loader({ request }: LoaderFunctionArgs) {
  const token = await getAppToken(request);
  if (token) {
    return redirect("/");
  }
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const action = formData.get("action") as string;
  if (action === "start-signin") {
    const email = formData.get("email") as string;
    const timeZone = (formData.get("timeZone") as string | null) ?? undefined;
    const locale = (formData.get("locale") as string | null) ?? undefined;
    await startEmailSignIn({ email, timeZone, locale });
    return {
      email,
    };
  }
  if (action === "verify-email") {
    const email = formData.get("email") as string;
    const code = formData.get("code") as string;
    try {
      return await signInWithEmailCode({ email, code });
    } catch (e) {
      return {
        email,
        error: true,
      };
    }
  }
}

export function SignIn() {
  const navigation = useNavigation();

  const [opt, setOpt] = useState(null as { timeZone: string; locale: string } | null);
  useEffect(() => {
    const options = Intl.DateTimeFormat().resolvedOptions();
    // Browser time zone and locale are only known on the client, so set them after hydration
    // oxlint-disable-next-line react/set-state-in-effect
    setOpt({ timeZone: options.timeZone, locale: options.locale });
  }, []);

  const canSubmit = navigation.state !== "submitting";

  return (
    <Form method="post" className="flex flex-col items-stretch gap-4 grow text-center">
      <Label htmlFor="email" className="text-lg">
        Sign in to Sumcast
      </Label>
      <Input name="email" type="email" placeholder="Your email" autoFocus />
      {opt?.timeZone && <Input name="timeZone" type="hidden" value={opt.timeZone} />}
      {opt?.locale && <Input name="locale" type="hidden" value={opt.locale} />}
      <Button type="submit" name="action" value="start-signin" disabled={!canSubmit} className="self-stretch">
        Continue with email
      </Button>
    </Form>
  );
}

export function VerifyEmail({ email }: { email: string }) {
  const navigation = useNavigation();
  const data = useActionData<typeof action>();
  const error = data && "error" in data ? data.error : false;
  const [code, setCode] = useState("");
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (navigation.state === "idle") {
      codeRef.current?.focus();
      codeRef.current?.select();
    }
  }, [navigation.state]);

  const canSubmit = /^\d{6}$/.test(code) && navigation.state !== "submitting";

  return (
    <Form method="post" className="flex flex-col gap-4 items-stretch grow text-center">
      <Label htmlFor="code" className="text-lg">
        Check your email
      </Label>
      <div className="text-sm text-muted-foreground  mb-2">
        We've sent you a verification code.
        <br />
        Please check your email at {email}.
      </div>
      {error && <div className="text-destructive text-sm">Sorry, the code is expired or invalid.</div>}
      <Input
        name="code"
        type="text"
        placeholder="Verification code"
        ref={codeRef}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        autoFocus
      />
      <Input name="email" type="hidden" value={email} />
      <Button type="submit" name="action" value="verify-email" className="mt-2 self-stretch" disabled={!canSubmit}>
        Sign in
      </Button>
    </Form>
  );
}

export default function SignInPage() {
  const data = useActionData<typeof action>();
  const email = data && "email" in data ? data.email : null;
  return (
    <div className="h-full w-full flex flex-col">
      <Header showSignIn={false} />
      <div className="grow flex flex-col">
        <div className="grow flex flex-row items-center justify-center px-4 py-16 w-sm max-h-[50rem] mx-auto">
          {email ? <VerifyEmail email={email} /> : <SignIn />}
        </div>
      </div>
    </div>
  );
}
